import { useState } from "react"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import { errorToast, toast } from "@/v2-ui/toast"
import Button, {
  INPUT_ALIGNED_BUTTON_CLASSES,
} from "@/v2-backoffice-client/components/button"
import { useLayerUpdateMutation } from "@/v2-backoffice-client/layer/update/layer.update.mutation"
import LayerFreshnessFields from "@/v2-backoffice-client/layer/view/LayerFreshnessFields"
import type { LayerFragment } from "@/v2-backoffice-client/layer/__types__/layer.fragment"

export type LayerViewDetailsExtrasProps = {
  layer: LayerFragment
}

/**
 * Thumbnail + declared freshness. Category membership lives on the
 * Availability tab with the other gates that decide who sees the layer.
 * Draw order and the in-category order are curated globally via the Layers
 * page's Organize dialog (a rank only makes sense relative to every other
 * layer); the layer's own rank still shows in the sheet's info grid.
 */
function LayerViewDetailsExtras(props: LayerViewDetailsExtrasProps) {
  const { layer } = props

  const [thumbnail, setThumbnail] = useState(layer.Thumbnail ?? "")

  const [updateLayer, updateState] = useLayerUpdateMutation()
  errorToast(updateState.error, { title: "Failed to update the thumbnail" })

  const isDirty = thumbnail !== (layer.Thumbnail ?? "")

  async function onSave() {
    const result = await updateLayer({
      variables: {
        input: {
          FeatureCode: layer.FeatureCode,
          Thumbnail: thumbnail || null,
        },
      },
    })
    if (result.data) toast({ title: "Thumbnail saved." })
  }

  return (
    // Thumbnail and freshness share one row; freshness wraps below when
    // the sheet is too narrow to fit both.
    <section className="flex flex-wrap items-start gap-x-10 gap-y-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="layer-details-thumbnail">Thumbnail</Label>
        <div className="flex items-center gap-2">
          {thumbnail ? (
            <img
              src={thumbnail}
              alt="Layer thumbnail"
              className="h-16 w-24 shrink-0 rounded-md border object-cover"
            />
          ) : null}
          <div className="flex items-center gap-2">
            <Input
              id="layer-details-thumbnail"
              className="w-72"
              placeholder="https://…"
              value={thumbnail}
              onChange={(event) => setThumbnail(event.target.value)}
            />
            <Button
              size="sm"
              variant="secondary"
              className={INPUT_ALIGNED_BUTTON_CLASSES}
              isLoading={updateState.loading}
              disabled={!isDirty}
              data-feature-code="UI_BackofficeLayers_Details_SaveThumbnail"
              onClick={() => void onSave()}
            >
              Save
            </Button>
          </div>
        </div>
      </div>

      <LayerFreshnessFields layerFeatureCode={layer.FeatureCode} />
    </section>
  )
}

export default LayerViewDetailsExtras
